import { useEffect, useMemo, useState } from "react";
import { Network } from "lucide-react";
import {
  buildGraphNodeSize,
  filterBuildGraphNodes,
  layoutBuildGraph,
  parseBuildGraph,
} from "../helpers/build-graph-layout";

type BuildGraphPreviewProps = {
  graph: unknown;
  initialTargets: string[];
  onNodeCountChange?: (count: BuildGraphNodeCount) => void;
};

export type BuildGraphNodeCount = {
  visible: number;
  total: number;
};

function edgePath(
  source: { x: number; y: number },
  target: { x: number; y: number },
) {
  const startX = source.x + buildGraphNodeSize.width;
  const startY = source.y + buildGraphNodeSize.height / 2;
  const endX = target.x;
  const endY = target.y + buildGraphNodeSize.height / 2;
  const controlOffset = Math.max(40, (endX - startX) / 2);

  return `M ${startX} ${startY} C ${startX + controlOffset} ${startY}, ${endX - controlOffset} ${endY}, ${endX} ${endY}`;
}

export function BuildGraphPreview({
  graph,
  initialTargets,
  onNodeCountChange,
}: BuildGraphPreviewProps) {
  const nodes = useMemo(() => parseBuildGraph(graph), [graph]);
  const targets = useMemo(
    () => [...new Set(nodes.map((node) => node.target))].sort(),
    [nodes],
  );
  const [showIgnoredNodes, setShowIgnoredNodes] = useState(false);
  const [visibleTargets, setVisibleTargets] = useState(
    () => new Set(initialTargets),
  );
  const visibleNodes = useMemo(
    () => filterBuildGraphNodes(nodes, visibleTargets, showIgnoredNodes),
    [nodes, showIgnoredNodes, visibleTargets],
  );
  useEffect(() => {
    onNodeCountChange?.({
      visible: visibleNodes.length,
      total: nodes.length,
    });
  }, [nodes.length, onNodeCountChange, visibleNodes.length]);
  const layout = useMemo(() => layoutBuildGraph(visibleNodes), [visibleNodes]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const selectedNode =
    layout.nodes.find((node) => node.id === selectedNodeId) ?? layout.nodes[0];
  const selectedConnections = new Set(
    selectedNode
      ? [
          selectedNode.id,
          ...selectedNode.dependencies,
          ...layout.edges
            .filter((edge) => edge.target.id === selectedNode.id)
            .map((edge) => edge.source.id),
        ]
      : [],
  );

  return (
    <div className="build-graph-viewer">
      {nodes.length ? (
        <div className="build-graph-content">
          <div className="build-graph-viewport">
            {visibleNodes.length ? (
              <div
                className="build-graph-canvas"
                style={{ height: layout.height, width: layout.width }}
              >
                <svg
                  aria-hidden="true"
                  height={layout.height}
                  viewBox={`0 0 ${layout.width} ${layout.height}`}
                  width={layout.width}
                >
                  <defs>
                    <marker
                      id="build-graph-arrow"
                      markerHeight="8"
                      markerWidth="8"
                      orient="auto"
                      refX="7"
                      refY="4"
                    >
                      <path d="M 0 0 L 8 4 L 0 8 z" />
                    </marker>
                  </defs>
                  {layout.edges.map((edge) => {
                    const highlighted =
                      selectedNode !== undefined &&
                      (edge.source.id === selectedNode.id ||
                        edge.target.id === selectedNode.id);
                    return (
                      <path
                        className={highlighted ? "selected" : undefined}
                        d={edgePath(edge.source, edge.target)}
                        key={`${edge.source.id}->${edge.target.id}`}
                        markerEnd="url(#build-graph-arrow)"
                      />
                    );
                  })}
                </svg>
                {layout.nodes.map((node) => {
                  const selected = node.id === selectedNode?.id;
                  const connected = selectedConnections.has(node.id);
                  return (
                    <button
                      aria-pressed={selected}
                      className={`build-graph-node${selected ? " selected" : ""}${connected ? " connected" : ""}`}
                      key={node.id}
                      onClick={() => setSelectedNodeId(node.id)}
                      style={{ left: node.x, top: node.y }}
                      type="button"
                    >
                      <strong>{node.projectName ?? node.projectDir}</strong>
                      <span>{node.target}</span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="build-graph-filter-empty">
                <Network />
                <p>No nodes match the current filters.</p>
              </div>
            )}
          </div>

          <aside className="build-graph-sidebar">
            <section className="build-graph-filters">
              <h4>Filters</h4>
              <label className="build-graph-filter-option">
                <input
                  checked={showIgnoredNodes}
                  onChange={(event) =>
                    setShowIgnoredNodes(event.target.checked)
                  }
                  type="checkbox"
                />
                <span>Show ignored nodes</span>
              </label>
              <fieldset>
                <legend>Targets</legend>
                {targets.map((target) => (
                  <label className="build-graph-filter-option" key={target}>
                    <input
                      checked={visibleTargets.has(target)}
                      onChange={(event) => {
                        setVisibleTargets((currentTargets) => {
                          const nextTargets = new Set(currentTargets);
                          if (event.target.checked) {
                            nextTargets.add(target);
                          } else {
                            nextTargets.delete(target);
                          }
                          return nextTargets;
                        });
                      }}
                      type="checkbox"
                    />
                    <span>{target}</span>
                  </label>
                ))}
              </fieldset>
            </section>

            {selectedNode && (
              <section className="build-node-details" aria-live="polite">
                <span>Selected node</span>
                <h4>{selectedNode.projectName ?? selectedNode.projectDir}</h4>
                {selectedNode.projectName && <p>{selectedNode.projectDir}</p>}
                <dl>
                  <dt>Target</dt>
                  <dd>{selectedNode.target}</dd>
                  <dt>Action</dt>
                  <dd>{selectedNode.action ?? "—"}</dd>
                  <dt>Build</dt>
                  <dd>{selectedNode.build ?? "—"}</dd>
                  <dt>Dependencies</dt>
                  <dd>{selectedNode.dependencies.length}</dd>
                  {selectedNode.phase && (
                    <>
                      <dt>Phase</dt>
                      <dd>{selectedNode.phase}</dd>
                    </>
                  )}
                </dl>
              </section>
            )}
          </aside>
        </div>
      ) : (
        <div className="build-graph-empty">
          <Network />
          <p>No graph was submitted for this build.</p>
        </div>
      )}
    </div>
  );
}
